/**
 * Typed-ish client for the Cloudflare Pages API.
 *
 * Every function returns parsed payload data and throws `ApiError` on failure,
 * so components never touch fetch/Response details directly.
 */

/** Error carrying the HTTP status returned by the API. */
export class ApiError extends Error {
  constructor(message, status = 500) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * @param {string} path
 * @param {{token?: string | null, method?: string, body?: unknown, signal?: AbortSignal}} [options]
 */
async function request(path, { token, method = 'GET', body, signal } = {}) {
  const headers = { accept: 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  let response;
  try {
    response = await fetch(path, {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ApiError('Network error — the API is unreachable', 0);
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(payload?.error || `Request failed (${response.status})`, response.status);
  }
  return payload;
}

/**
 * The auto-suggest index for the search bar.
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function fetchTickerIndex(signal) {
  const payload = await request('/api/stocks', { signal });
  return payload?.data ?? [];
}

/**
 * Full precomputed analysis for one ticker.
 * @param {string} ticker
 * @param {{signal?: AbortSignal}} [options]
 */
export async function fetchStock(ticker, { signal } = {}) {
  const payload = await request(`/api/stock/${encodeURIComponent(ticker)}`, { signal });
  return payload?.data ?? null;
}

/**
 * The signed-in user's saved analyses.
 * @param {string} token
 */
export async function fetchHistory(token) {
  const payload = await request('/api/history', { token });
  return payload?.data ?? [];
}

/**
 * Save (or refresh) a ticker in the user's history.
 * @param {string} token
 * @param {string} ticker
 */
export async function saveToHistory(token, ticker) {
  const payload = await request('/api/history', {
    token,
    method: 'POST',
    body: { ticker },
  });
  return payload?.data ?? null;
}

/**
 * Remove a ticker from the user's history.
 * @param {string} token
 * @param {string} ticker
 */
export async function deleteFromHistory(token, ticker) {
  const payload = await request(`/api/history?ticker=${encodeURIComponent(ticker)}`, {
    token,
    method: 'DELETE',
  });
  return payload?.data ?? [];
}
